import React, { useState, useEffect } from 'react';
import { Upload, FileText, X, Download, Trash2, Eye } from 'lucide-react';
import { documentsAPI } from '../services/api';
import toast from 'react-hot-toast';

const DocumentUpload = ({ eventId, compact = false }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (eventId) {
      loadDocuments();
    }
  }, [eventId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentsAPI.getEventDocuments(eventId);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not allowed. Please upload PDF, Word, Excel, or image files.');
      return;
    }

    // Max file size: 10MB
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      await documentsAPI.uploadDocument(eventId, formData);
      toast.success('Document uploaded successfully');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to upload document');
      console.error('Error uploading document:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (documentId, filename, fileType) => {
    try {
      const response = await documentsAPI.downloadDocument(documentId);
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: fileType });
      
      // Create a URL and open in new tab
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      toast.error('Failed to view document');
      console.error('Error viewing document:', error);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await documentsAPI.downloadDocument(documentId);
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      toast.error('Failed to download document');
      console.error('Error downloading document:', error);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await documentsAPI.deleteDocument(documentId);
      toast.success('Document deleted successfully');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
      console.error('Error deleting document:', error);
    }
  };

  const getFileIcon = () => <FileText size={16} aria-hidden="true" />;

  if (compact) {
    return (
      <div style={{ marginTop: '5px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontSize: '14px' }}>({documents.length} files)</span>
          <label 
            htmlFor="compact-file-input" 
            className="btn btn-sm" 
            style={{ cursor: 'pointer', padding: '3px 8px', fontSize: '11px' }}
          >
            <Upload size={12} style={{ marginRight: '3px' }} />
            Add
          </label>
          <input
            id="compact-file-input"
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileInput}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
          />
        </div>
        
        {/* Compact Documents List */}
        {documents.length > 0 && (
          <div style={{ fontSize: '12px' }}>
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  padding: '6px 8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  marginBottom: '4px',
                  backgroundColor: '#fff'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    flex: '1 1 auto',
                    minWidth: '0'
                  }}>
                    <span style={{ marginRight: '6px', flexShrink: 0 }}>{getFileIcon(doc.file_type)}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.filename}
                    </span>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    gap: '4px',
                    flexShrink: 0,
                    marginLeft: 'auto'
                  }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '6px 10px', fontSize: '12px' }}
                      onClick={() => handleView(doc.id, doc.filename, doc.file_type)}
                      title="View"
                    >
                      <Eye size={14} style={{ marginRight: '4px' }} />
                      View
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '6px 10px', fontSize: '12px' }}
                      onClick={() => handleDownload(doc.id, doc.filename)}
                      title="Download"
                    >
                      <Download size={14} style={{ marginRight: '4px' }} />
                      Download
                    </button>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '6px 10px', fontSize: '12px' }}
                      onClick={() => handleDelete(doc.id)}
                      title="Delete"
                    >
                      <Trash2 size={14} style={{ marginRight: '4px' }} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: '20px' }}>
      <h3 style={{ marginBottom: '15px' }}>Event Documents</h3>
      
      {/* Upload Area */}
      <div
        className={`upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{
          border: '2px dashed #6b7280',
          borderRadius: '8px',
          padding: '30px',
          textAlign: 'center',
          backgroundColor: dragActive ? '#f3f4f6' : '#fafafa',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          marginBottom: '20px'
        }}
        onClick={() => document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileInput}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
        />
        
        <Upload size={40} style={{ marginBottom: '10px', color: '#6b7280' }} />
        <p style={{ marginBottom: '5px', fontWeight: '600' }}>
          {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
        </p>
        <p style={{ fontSize: '12px', color: '#6b7280' }}>
          PDF, Word, Excel, or Image files (max 10MB)
        </p>
      </div>

      {/* Documents List */}
      {loading ? (
        <p>Loading documents...</p>
      ) : documents.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#6b7280' }}>No documents uploaded yet</p>
      ) : (
        <div className="documents-list">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="document-item"
              style={{
                padding: '10px 15px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                marginBottom: '8px',
                backgroundColor: '#fff'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flex: '1 1 auto',
                  minWidth: '0'
                }}>
                  <span style={{ fontSize: '20px', marginRight: '10px', flexShrink: 0 }}>
                    {getFileIcon(doc.file_type)}
                  </span>
                  <div style={{ minWidth: '0' }}>
                    <p style={{ margin: 0, fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.filename}
                    </p>
                    <p style={{ margin: 0, fontSize: '11px', color: '#6b7280' }}>
                      {new Date(doc.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  gap: '8px',
                  flexShrink: 0,
                  marginLeft: 'auto'
                }}>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '8px 16px', fontSize: '14px' }}
                    onClick={() => handleView(doc.id, doc.filename, doc.file_type)}
                    title="View"
                  >
                    <Eye size={16} style={{ marginRight: '6px' }} />
                    View
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '8px 16px', fontSize: '14px' }}
                    onClick={() => handleDownload(doc.id, doc.filename)}
                    title="Download"
                  >
                    <Download size={16} style={{ marginRight: '6px' }} />
                    Download
                  </button>
                  <button
                    className="btn btn-danger"
                    style={{ padding: '8px 16px', fontSize: '14px' }}
                    onClick={() => handleDelete(doc.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} style={{ marginRight: '6px' }} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
